// src/DashboardsTestPage.tsx
import { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";

/* ----------------------------- shared utils ----------------------------- */

const BG_DARK = "#083264";
const BG_PURPLE = "#083264";

const nf = new Intl.NumberFormat("ru-RU");


type AuthPayload = {
  access_token?: string;
  iin?: string;
};

type RoleResponse = { role?: string };
type OrgOption = { id: string; label: string };

type TestCountersResponse = {
  mis_id?: string;
  display_years?: number[];
  yearly: Array<{
    year: number;
    app_total: number;
    hospital_total: number;
    hospital_ks_total?: number;
    hospital_ds_total?: number;
    mother_total?: number;
    queue_total: number;
    duchet_total?: number;
    vaccination_total?: number;
  }>;
  last_3_years: {
    years: number[];
    app_total: number;
    hospital_total: number;
    hospital_ks_total?: number;
    hospital_ds_total?: number;
    mother_total?: number;
    queue_total: number;
    duchet_total?: number;
    vaccination_total?: number;
  };
};

type YearValue = {
  year: number;
  value: number;
};

type TestCard = {
  title: string;
  value: number;
  max: number;
  unit?: string;
  odometerLabel: string;
  yearlyRows: YearValue[];
  href: string;
  disabled?: boolean;
};

function getDashboardsApiBase(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return String(env?.VITE_DASHBOARDS_API_BASE || "/dashboards-api").replace(/\/+$/, "");
}

function getDashboardsPublicBase(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const configured = String(
    env?.VITE_DASHBOARDS_PUBLIC_BASE_URL ||
      env?.VITE_DASHBOARDS_PUBLIC_BASE ||
      "",
  ).replace(/\/+$/, "");

  return configured || window.location.origin;
}

function buildDashboardsPageUrl(path: string, params?: Record<string, string | undefined>): string {
  const full = new URL(path, getDashboardsPublicBase());
  Object.entries(params || {}).forEach(([key, value]) => {
    if (!value) return;
    full.searchParams.set(key, value);
  });
  return full.toString();
}

function looksLikeMojibakeLabel(label: string): boolean {
  const s = String(label || "").trim();
  if (!s) return false;
  return /(?:\u0420.|\u0421.|\u00D0.|\u00D1.){3,}/.test(s) || s.includes("���");
}

function getAuthPayload(): AuthPayload | null {
  try {
    const raw = localStorage.getItem("auth_payload");
    if (!raw) return null;
    return JSON.parse(raw) as AuthPayload;
  } catch {
    return null;
  }
}

async function fetchUserRoleFromDefektolog(): Promise<string> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers.Authorization = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;

  const res = await fetch(`/api/v1/analytics/role`, { headers, credentials: "omit" });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `${res.status} ${res.statusText}`);
  }
  const data = (await res.json()) as RoleResponse;
  return String(data?.role || "").trim();
}

async function fetchUserOrgsFromDefektolog(): Promise<OrgOption[]> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers.Authorization = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;

  const res = await fetch(`/api/v1/analytics/dicts`, { headers, credentials: "omit" });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `${res.status} ${res.statusText}`);
  }
  const data = (await res.json()) as { orgs?: Array<Record<string, unknown>> };
  const orgs = Array.isArray(data?.orgs) ? data.orgs : [];
  return orgs
    .map((org) => {
      const id = String(org?.orgId || "").trim();
      if (!id) return null;
      const label = String(org?.name || org?.orgName || org?.shortName || id).trim();
      const safeLabel = !label || looksLikeMojibakeLabel(label) ? id : label;
      return { id, label: safeLabel || id };
    })
    .filter((v): v is OrgOption => Boolean(v?.id));
}

async function loadTestCounters(misId?: string): Promise<TestCountersResponse> {
  const auth = getAuthPayload();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers.Authorization = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;
  const role = (localStorage.getItem("dashboards_role") || "").trim();
  if (role) headers["X-Role"] = role;

  const query = misId ? `?mis_id=${encodeURIComponent(misId)}` : "";

  const res = await fetch(`${getDashboardsApiBase()}/api/v1/app/test-counters${query}`, {
    headers,
    credentials: "omit",
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as TestCountersResponse;
}

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(n, b));
}

function fmtOdo(n: number, digits = 8) {
  const s = String(Math.max(0, Math.floor(n)));
  return s.padStart(digits, "0");
}

/* ----------------------------- smooth gradient stops (red->yellow->green) ----------------------------- */

function hexToRgb(hex: string) {
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex(r: number, g: number, b: number) {
  const to = (x: number) => x.toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}
function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}
function lerpHex(a: string, b: string, t: number) {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  const r = Math.round(lerp(A.r, B.r, t));
  const g = Math.round(lerp(A.g, B.g, t));
  const bb = Math.round(lerp(A.b, B.b, t));
  return rgbToHex(r, g, bb);
}
function makeSmoothStops(steps = 220): Array<[number, string]> {
  const red = "#FF1744";
  const yellow = "#FFEB3B";
  const green = "#00E676";

  const out: Array<[number, string]> = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const c = t <= 0.5 ? lerpHex(red, yellow, t / 0.5) : lerpHex(yellow, green, (t - 0.5) / 0.5);
    out.push([t, c]);
  }
  return out;
}

/* ----------------------------- TEST 1 (odometer) ----------------------------- */

type GaugeCardProps = {
  title: string;
  value: number;
  max: number;
  unit?: string;
  odometerLabel: string;
  yearlyRows: YearValue[];
  href: string;
  disabled?: boolean;
};

function makeOdometerGraphics(params: { odometerLabel: string; yearlyRows: YearValue[]; digits?: number }) {
  const digits = params.digits ?? 8;

  const rows = params.yearlyRows.slice(0, 3);
  const rowsText = rows.map((row) => fmtOdo(row.value, digits));
  const rowsChars = rowsText.map((t) => t.split(""));

  // РІСљвЂ¦ РЎв‚¬Р С‘РЎР‚Р С‘Р Р…Р В° Р С•Р Т‘Р С•Р СР ВµРЎвЂљРЎР‚Р В° = Р С—РЎР‚Р С‘Р СР ВµРЎР‚Р Р…Р С• Р Р…Р В° 20% РЎС“Р В¶Р Вµ (РЎвЂЎР ВµРЎР‚Р ВµР В· scaleX=0.8 РЎС“ Р С–РЎР‚РЎС“Р С—Р С—РЎвЂ№)
  const scaleX = 0.8;

  const yearW = 54;
  const cellW = 18;
  const cellH = 30;
  const gap = 4;
  const rowGap = 8;

  const labelH = 10;

  // РІСљвЂ¦ (1) Р С•Р Т‘Р С‘Р Р…Р В°Р С”Р С•Р Р†РЎвЂ№Р Вµ Р С•РЎвЂљРЎРѓРЎвЂљРЎС“Р С—РЎвЂ№: РЎРѓР В»Р ВµР Р†Р В° Р Т‘Р С• Р В»Р ВµРЎвЂљ Р С‘ РЎРѓР С—РЎР‚Р В°Р Р†Р В° Р Т‘Р С• РЎвЂ Р С‘РЎвЂћРЎР‚ РІР‚вЂќ Р С•Р Т‘Р С‘Р Р…Р В°Р С”Р С•Р Р†РЎвЂ№Р Вµ
  const padX = 14;
  const padTop = 10;
  const padBottom = 10;

  // РІСљвЂ¦ (4) РЎР‚Р В°РЎРѓРЎРѓРЎвЂљР С•РЎРЏР Р…Р С‘Р Вµ Р С•РЎвЂљ Р В»Р ВµРЎвЂљ Р Т‘Р С• Р Р…Р В°РЎвЂЎР В°Р В»Р В° РЎвЂ Р С‘РЎвЂћРЎР‚ Р С•Р Т‘Р С•Р СР ВµРЎвЂљРЎР‚Р В° = РЎР‚Р В°РЎРѓРЎРѓРЎвЂљР С•РЎРЏР Р…Р С‘РЎР‹ Р С•РЎвЂљ Р С”РЎР‚Р В°РЎРЏ Р Т‘Р С• Р В»Р ВµРЎвЂљ
  const gapMid = padX;

  const digitsW = digits * cellW + (digits - 1) * gap;
  const groupW = padX + yearW + gapMid + digitsW + padX;
  const rowsH = 3 * cellH + 2 * rowGap;
  const groupH = padTop + labelH + 6 + rowsH + padBottom;

  const startX = -groupW / 2;
  const startY = -groupH / 2;

  const dividerX = (x: number, y: number) => ({
    type: "rect" as const,
    shape: { x, y, width: 3, height: cellH - 6, r: 1 },
    style: { fill: "rgba(255,255,255,0.28)" },
    silent: true,
  });

  const graphics: any[] = [];

  graphics.push({
    type: "group",
    left: "center",
    // РІСљвЂ¦ (3) Р Р…Р С‘Р В¶Р Р…Р С‘Р в„– Р С•РЎвЂљРЎРѓРЎвЂљРЎС“Р С— Р Т‘Р С• Р В±Р В»Р С•Р С”Р В° Р С•Р Т‘Р С•Р СР ВµРЎвЂљРЎР‚Р В° = Р Р†Р ВµРЎР‚РЎвЂ¦Р Р…Р ВµР СРЎС“ Р С•РЎвЂљРЎРѓРЎвЂљРЎС“Р С—РЎС“ Р Т‘Р С• title (top: 12)
    bottom: 12,
    scale: [scaleX, 1],
    children: [
      {
        type: "rect",
        shape: { x: startX, y: startY, width: groupW, height: groupH, r: 12 },
        style: {
          fill: "rgba(8,12,26,0.62)",
          stroke: "rgba(255,255,255,0.48)",
          lineWidth: 3,
          shadowBlur: 22,
          shadowColor: "rgba(0,0,0,0.45)",
        },
        silent: true,
      },
      {
        type: "rect",
        shape: { x: startX + 1, y: startY + 1, width: groupW - 2, height: 16, r: 11 },
        style: { fill: "rgba(255,255,255,0.06)" },
        silent: true,
      },
      {
        type: "text",
        style: {
          text: params.odometerLabel.toUpperCase(),
          x: 0,
          y: startY + padTop,
          fill: "rgba(255,255,255,0.68)",
          align: "center",
          font: "800 7px system-ui, -apple-system, Segoe UI, Roboto, Arial",
        },
        silent: true,
      },
    ],
  });

  const children: any[] = [];
  const rowsTop = startY + padTop + labelH + 6;

  const yearsLeft = startX + padX;
  const digitsLeft = yearsLeft + yearW + gapMid;

  for (let r = 0; r < 3; r++) {
    const rowY = rowsTop + r * (cellH + rowGap);

    children.push({
      type: "text",
      style: {
        text: String(rows[r]?.year ?? ""),
        x: yearsLeft,
        y: rowY + 7,
        fill: "rgba(255,255,255,0.76)",
        align: "left",
        font: "900 12px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
      },
      silent: true,
    });

    for (let i = 0; i < digits; i++) {
      const x = digitsLeft + i * (cellW + gap);
      const y = rowY;

      children.push({
        type: "rect",
        shape: { x, y, width: cellW, height: cellH, r: 7 },
        style: {
          fill: "rgba(0,0,0,0.58)",
          stroke: "rgba(255,255,255,0.52)",
          lineWidth: 3,
          shadowBlur: 10,
          shadowColor: "rgba(0,0,0,0.35)",
        },
        silent: true,
      });

      children.push({
        type: "rect",
        shape: { x: x + 1, y: y + 1, width: cellW - 2, height: Math.floor(cellH * 0.42), r: 6 },
        style: { fill: "rgba(255,255,255,0.08)" },
        silent: true,
      });

      children.push({
        type: "rect",
        shape: { x: x + 2, y: y + Math.floor(cellH / 2) - 1, width: cellW - 4, height: 2, r: 1 },
        style: { fill: "rgba(255,255,255,0.12)" },
        silent: true,
      });

      children.push(dividerX(x + 3, y + 3));
      children.push(dividerX(x + cellW - 6, y + 3));

      children.push({
        type: "text",
        style: {
          text: rowsChars[r]?.[i] ?? "",
          x: x + cellW / 2,
          y: y + 6,
          fill: "#EAFBFF",
          align: "center",
          font:
            "900 20px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
          textShadowColor: "rgba(255,255,255,0.18)",
          textShadowBlur: 8,
        },
        silent: true,
      });
    }
  }

  graphics[0].children.push(...children);
  return graphics;
}

function GaugeCardTest1({
  title,
  value,
  max,
  unit = "%",
  odometerLabel,
  yearlyRows,
  href,
  disabled = false,
}: GaugeCardProps) {
  const safe = Number.isFinite(value) ? clamp(value, 0, max) : 0;
  const handleOpen = () => {
    if (disabled) return;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const option = useMemo(() => {
    const v01 = max > 0 ? safe / max : 0;
    const smoothStops = makeSmoothStops(260);

    return {
      backgroundColor: "transparent",
      animation: true,
      animationDuration: 850,
      animationEasing: "cubicOut",
      tooltip: { show: false },
      series: [
        {
          type: "gauge",
          startAngle: 210,
          endAngle: -30,
          min: 0,
          max,

          radius: "82%",
          // РІСљвЂ¦ (1) РЎС“Р СР ВµР Р…РЎРЉРЎв‚¬Р С‘РЎвЂљРЎРЉ РЎР‚Р В°РЎРѓРЎРѓРЎвЂљР С•РЎРЏР Р…Р С‘Р Вµ Р СР ВµР В¶Р Т‘РЎС“ title Р С‘ РЎРѓР С—Р С‘Р Т‘Р С•Р СР ВµРЎвЂљРЎР‚Р С•Р С Р Р† 2 РЎР‚Р В°Р В·Р В°: РЎРѓР С—Р С‘Р Т‘Р С•Р СР ВµРЎвЂљРЎР‚ Р Р†РЎвЂ№РЎв‚¬Р Вµ
          center: ["50%", "47%"],
          splitNumber: 10,

          axisLine: {
            lineStyle: {
              width: 36,
              color: smoothStops,
              shadowBlur: 20,
              shadowColor: "rgba(0,0,0,0.25)",
            },
          },

          axisLabel: { show: false },
          axisTick: { distance: -30, length: 10, lineStyle: { color: "rgba(255,255,255,0.70)", width: 2 } },
          splitLine: { distance: -30, length: 18, lineStyle: { color: "rgba(255,255,255,0.92)", width: 3 } },

          pointer: { length: "66%", width: 6 },
          itemStyle: { color: "#EAFBFF", shadowBlur: 22, shadowColor: "rgba(255,255,255,0.25)" },

          progress: {
            show: true,
            width: 10,
            roundCap: true,
            itemStyle: { color: `rgba(255,255,255,${0.05 + 0.10 * v01})` },
          },

          detail: {
            valueAnimation: true,
            // РІСљвЂ¦ (2) РЎвЂ Р С‘РЎвЂћРЎР‚РЎвЂ№ Р Р†Р Р…РЎС“РЎвЂљРЎР‚Р С‘ РЎРѓР С—Р С‘Р Т‘Р С•Р СР ВµРЎвЂљРЎР‚Р В° Р Р†Р Р…Р С‘Р В· Р Р…Р В° 20px
            offsetCenter: [0, "22%"],
            color: "#ffffff",
            fontSize: 34,
            fontWeight: 900,
            formatter: (v: number) => nf.format(Math.round(v)),
          },

          data: [{ value: safe }],
          emphasis: { scale: true },
        },
      ],

      graphic: [
        {
          type: "text",
          left: "center",
          top: 12,
          style: {
            text: title,
            fill: "rgba(255,255,255,0.92)",
            align: "center",
            font: "900 21px system-ui, -apple-system, Segoe UI, Roboto, Arial",
          },
        },
        ...makeOdometerGraphics({ odometerLabel, yearlyRows, digits: 8 }),
      ],
    };
  }, [title, safe, max, unit, odometerLabel, yearlyRows]);

  return (
    <div
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleOpen();
        }
      }}
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? -1 : 0}
      style={{
        borderRadius: 16,
        padding: 12,
        border: "1px solid rgba(255,255,255,0.10)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
        boxShadow: "0 18px 56px rgba(0,0,0,0.45)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        overflow: "hidden",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {/* Р Р†РЎвЂ№РЎРѓР С•РЎвЂљР В° Р С•РЎРѓРЎвЂљР В°Р Р†Р В»Р ВµР Р…Р В° Р С”Р В°Р С” Р В±РЎвЂ№Р В»Р В° Р Р† РЎвЂљР Р†Р С•Р ВµР в„– Р Р†Р ВµРЎР‚РЎРѓР С‘Р С‘ */}
      <ReactECharts option={option} style={{ height: 500, width: "100%" }} notMerge={true} lazyUpdate={true} />
    </div>
  );
}

/* ----------------------------- TEST 2 / TEST 3 (no odometer, half gauge + bar + delta) ----------------------------- */

type GaugeHalfCardProps = {
  title: string;
  value: number;
  max: number;
  deltaPct: number;
  deltaSuffix: string;
};

function GaugeHalfCard({ title, value, max, deltaPct, deltaSuffix }: GaugeHalfCardProps) {
  const safe = Number.isFinite(value) ? clamp(value, 0, max) : 0;
  const v01 = max > 0 ? safe / max : 0;

  const ACTIVE = "#449e9f";
  const INACTIVE = "rgba(255,255,255,0.10)";

  const deltaColor = deltaPct >= 0 ? "#35d07f" : "#ff4d6d";
  const deltaArrow = deltaPct >= 0 ? "РІвЂ“Р†" : "РІвЂ“С";
  const deltaText = `${deltaArrow} ${Math.abs(Math.round(deltaPct))}% ${deltaSuffix}`;

  const option = useMemo(() => {
    const barX = 26;
    const barY = 268;
    const barW = 300;
    const barH = 6;

    const pointerBottom = -55;
    const pointerTop = -165;

    return {
      backgroundColor: "transparent",
      animation: true,
      animationDuration: 650,
      animationEasing: "cubicOut",
      tooltip: { show: false },
      series: [
        {
          type: "gauge",
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max,
          radius: "78%",
          center: ["50%", "58%"],

          axisLabel: { show: false },
          splitNumber: 12,

          axisLine: {
            lineStyle: {
              width: 14,
              color: [
                [v01, ACTIVE],
                [1, INACTIVE],
              ],
              shadowBlur: 14,
              shadowColor: "rgba(0,0,0,0.28)",
            },
          },

          axisTick: { show: false },
          splitLine: { show: false },

          pointer: {
            length: "72%",
            width: 4,
            icon: `path://M-2,${pointerBottom} L2,${pointerBottom} L2,${pointerTop} L-2,${pointerTop} Z`,
          },
          itemStyle: {
            color: "rgba(240,250,255,0.95)",
            shadowBlur: 16,
            shadowColor: "rgba(255,255,255,0.18)",
          },

          progress: { show: false },

          detail: {
            valueAnimation: true,
            offsetCenter: [0, "12%"],
            color: "rgba(255,255,255,0.90)",
            fontSize: 44,
            fontWeight: 800,
            formatter: (v: number) => nf.format(Math.round(v)),
          },

          data: [{ value: safe }],
          emphasis: { scale: false },
        },
      ],

      graphic: [
        {
          type: "text",
          left: 18,
          top: 16,
          style: {
            text: title,
            fill: "rgba(255,255,255,0.88)",
            font: "700 18px system-ui, -apple-system, Segoe UI, Roboto, Arial",
          },
        },

        { type: "rect", shape: { x: barX, y: barY, width: barW, height: barH, r: 6 }, style: { fill: "rgba(255,255,255,0.10)" }, silent: true },
        {
          type: "rect",
          shape: { x: barX, y: barY, width: Math.max(6, Math.floor(barW * v01)), height: barH, r: 6 },
          style: { fill: ACTIVE, shadowBlur: 10, shadowColor: "rgba(68,158,159,0.35)" },
          silent: true,
        },

        {
          type: "text",
          left: "center",
          top: barY + 22,
          style: {
            text: deltaText,
            fill: deltaColor,
            align: "center",
            font: "700 16px system-ui, -apple-system, Segoe UI, Roboto, Arial",
          },
        },
      ],
    };
  }, [safe, max, v01, title, deltaText, deltaColor]);

  return (
    <div
      style={{
        borderRadius: 16,
        padding: 12,
        border: "1px solid rgba(255,255,255,0.10)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
        boxShadow: "0 18px 56px rgba(0,0,0,0.45)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        overflow: "hidden",
      }}
    >
      <ReactECharts option={option} style={{ height: 340, width: "100%" }} notMerge={true} lazyUpdate={true} />
    </div>
  );
}

/* ----------------------------- pages switcher ----------------------------- */

function PageShell(props: { children: React.ReactNode; title: string }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: 20,
        boxSizing: "border-box",
        color: "white",
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial",
        background: `
          radial-gradient(1100px 540px at 50% 10%, rgba(0,184,186,0.20), transparent 60%),
          radial-gradient(900px 520px at 15% 30%, rgba(126,87,194,0.20), transparent 55%),
          radial-gradient(900px 520px at 85% 30%, rgba(224,56,0,0.12), transparent 55%),
          linear-gradient(180deg, ${BG_DARK} 0%, ${BG_PURPLE} 100%)
        `,
      }}
    >
      {props.title ? <div style={{ maxWidth: 1200, margin: "0 auto 14px auto", fontSize: 18, fontWeight: 900 }}>{props.title}</div> : null}

      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 14,
        }}
      >
        {props.children}
      </div>
    </div>
  );
}

/* ----------------------------- TEST 1 PAGE ----------------------------- */

function Test1Page(): JSX.Element {
  const [payload, setPayload] = useState<TestCountersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState<string>("");
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [misId, setMisId] = useState<string>("");
  const [ready, setReady] = useState(false);

  const isSuperadmin = role === "SUPERADMIN";

  useEffect(() => {
    const urlMisId = new URLSearchParams(window.location.search).get("mis_id") || "";
    (async () => {
      try {
        const fetchedRole = await fetchUserRoleFromDefektolog();
        const nextRole = fetchedRole || (localStorage.getItem("dashboards_role") || "").trim();
        setRole(nextRole);
        if (nextRole) localStorage.setItem("dashboards_role", nextRole);

        const fetchedOrgs = await fetchUserOrgsFromDefektolog();
        setOrgs(fetchedOrgs);

        const effectiveMis = urlMisId || fetchedOrgs[0]?.id || "";
        setMisId(effectiveMis);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setReady(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (!role) return;
    if (!misId) return;
    setLoading(true);
    setError(null);
    loadTestCounters(misId || undefined)
      .then((data) => setPayload(data))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [isSuperadmin, misId, role]);

  const cards = useMemo<TestCard[]>(() => {
    const yearly = payload?.yearly ?? [];
    const displayYears =
      payload?.display_years?.length === 3
        ? payload.display_years
        : [new Date().getFullYear() - 2, new Date().getFullYear() - 1, new Date().getFullYear()];
    const yearlyByYear = new Map(yearly.map((row) => [row.year, row]));

    const appRows = displayYears.map((year) => ({
      year,
      value: yearlyByYear.get(year)?.app_total ?? 0,
    }));
    const ksRows = displayYears.map((year) => ({
      year,
      value: yearlyByYear.get(year)?.hospital_ks_total ?? 0,
    }));
    const dsRows = displayYears.map((year) => ({
      year,
      value: yearlyByYear.get(year)?.hospital_ds_total ?? 0,
    }));
    const motherRows = displayYears.map((year) => ({
      year,
      value: yearlyByYear.get(year)?.mother_total ?? 0,
    }));
    // Д-учёт — карты, взятые на учёт за год; вакцинация — проведённые прививки.
    // Раньше плашка Д-учёта стояла с жёстким value: 0 и пустой разбивкой.
    const duchetRows = displayYears.map((year) => ({
      year,
      value: yearlyByYear.get(year)?.duchet_total ?? 0,
    }));
    const vacRows = displayYears.map((year) => ({
      year,
      value: yearlyByYear.get(year)?.vaccination_total ?? 0,
    }));

    const app3 = appRows.reduce((sum, row) => sum + row.value, 0);
    const ks3 = ksRows.reduce((sum, row) => sum + row.value, 0);
    const ds3 = dsRows.reduce((sum, row) => sum + row.value, 0);
    const mother3 = motherRows.reduce((sum, row) => sum + row.value, 0);
    const duchet3 = duchetRows.reduce((sum, row) => sum + row.value, 0);
    const vac3 = vacRows.reduce((sum, row) => sum + row.value, 0);

    // ШКАЛА У КАЖДОЙ ПЛАШКИ СВОЯ.
    //
    // Была одна общая на всех, от наибольшего значения: у поликлиники 6,4 млн
    // документов, у дневного стационара 6,5 тыс. — то есть в тысячу раз меньше.
    // Стрелки всех плашек, кроме поликлиники, лежали на нуле и ничего не показывали.
    //
    // Ориентир каждой плашки — её собственный ЛУЧШИЙ ГОД, умноженный на три:
    // «сколько было бы, если бы все три года шли как лучший». Стрелка показывает,
    // насколько трёхлетний итог (то самое число в центре) до этого дотягивает.
    // Шкала привязана к самой плашке, поэтому сравнение честное внутри неё
    // и не зависит от того, что происходит у соседей.
    const scaleFor = (rows: { year: number; value: number }[]) => {
      const best = Math.max(0, ...rows.map((r) => r.value));
      return Math.max(1, Math.ceil(best * rows.length * 1.15));
    };
    const disabled = !misId;

    return [
      {
        title: "\u041f\u043e\u043b\u0438\u043a\u043b\u0438\u043d\u0438\u043a\u0430",
        value: app3,
        max: scaleFor(appRows),
        unit: "",
        odometerLabel: "\u041f\u043e\u0441\u043b\u0435\u0434\u043d\u0438\u0435 3 \u0433\u043e\u0434\u0430",
        yearlyRows: appRows,
        href: buildDashboardsPageUrl("/dashboards/app", {
          mis_id: misId || undefined,
        }),
        disabled,
      },
      {
        title: "\u041a\u0440\u0443\u0433\u043b\u043e\u0441\u0443\u0442\u043e\u0447\u043d\u044b\u0439 \u0441\u0442\u0430\u0446\u0438\u043e\u043d\u0430\u0440",
        value: ks3,
        max: scaleFor(ksRows),
        unit: "",
        odometerLabel: "\u041f\u043e\u0441\u043b\u0435\u0434\u043d\u0438\u0435 3 \u0433\u043e\u0434\u0430",
        yearlyRows: ksRows,
        href: buildDashboardsPageUrl("/dashboards/hospital", {
          mis_id: misId || undefined,
          place: "\u041a\u0421",
        }),
        disabled,
      },
      {
        title: "\u0414\u043d\u0435\u0432\u043d\u043e\u0439 \u0441\u0442\u0430\u0446\u0438\u043e\u043d\u0430\u0440",
        value: ds3,
        max: scaleFor(dsRows),
        unit: "",
        odometerLabel: "\u041f\u043e\u0441\u043b\u0435\u0434\u043d\u0438\u0435 3 \u0433\u043e\u0434\u0430",
        yearlyRows: dsRows,
        href: buildDashboardsPageUrl("/dashboards/hospital", {
          mis_id: misId || undefined,
          place: "\u0414\u0421",
        }),
        disabled,
      },
      {
        title: "\u041e\u0445\u0440\u0430\u043d\u0430 \u043c\u0430\u0442\u0435\u0440\u0438\u043d\u0441\u0442\u0432\u0430",
        value: mother3,
        max: scaleFor(motherRows),
        unit: "",
        odometerLabel: "\u041f\u043e\u0441\u043b\u0435\u0434\u043d\u0438\u0435 3 \u0433\u043e\u0434\u0430",
        yearlyRows: motherRows,
        href: buildDashboardsPageUrl("/dashboards/mother-hospital", {
          mis_id: misId || undefined,
        }),
        disabled,
      },
      {
        title: "Диспансерный учёт",
        value: duchet3,
        max: scaleFor(duchetRows),
        unit: "",
        odometerLabel: "Последние 3 года",
        yearlyRows: duchetRows,
        href: buildDashboardsPageUrl("/dashboards/duchet", {
          mis_id: misId || undefined,
        }),
        disabled,
      },
      {
        title: "Вакцинация",
        value: vac3,
        max: scaleFor(vacRows),
        unit: "",
        odometerLabel: "Последние 3 года",
        yearlyRows: vacRows,
        href: buildDashboardsPageUrl("/dashboards/vaccination", {
          mis_id: misId || undefined,
        }),
        disabled,
      },
    ];
  }, [misId, payload]);

  if (!ready) {
    return <PageShell title="">Загрузка...</PageShell>;
  }

  if (loading) {
    return <PageShell title="">Загрузка...</PageShell>;
  }

  if (error) {
    return <PageShell title="">{error}</PageShell>;
  }

  return (
    <PageShell title="">
      {isSuperadmin ? (
        <div
          style={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 4,
            fontWeight: 700,
          }}
        >
          <span>Подразделение:</span>
          <select
            value={misId}
            onChange={(e) => setMisId(e.target.value)}
            style={{
              minWidth: 320,
              height: 34,
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.35)",
              background: "#083264",
              color: "#fff",
              padding: "0 8px",
            }}
          >
            <option value="">Все подразделения</option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {cards.map((c) => (
        <GaugeCardTest1 key={c.title} {...c} />
      ))}
    </PageShell>
  );
}

/* ----------------------------- TEST 2 PAGE ----------------------------- */

function Test2Page(): JSX.Element {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1400);
    return () => window.clearInterval(id);
  }, []);

  const cards = useMemo<GaugeHalfCardProps[]>(() => {
    const max = 10_000_000;

    const base = [
      { title: "Р С™Р С•Р В»Р С‘РЎвЂЎР ВµРЎРѓРЎвЂљР Р†Р С• РЎС“РЎРѓР В»РЎС“Р С– Р С’Р СџР Сџ", value: 6_200_000, deltaPct: +12, deltaSuffix: "Р С” Р С—РЎР‚Р С•РЎв‚¬Р В»Р С•Р в„– Р Р…Р ВµР Т‘Р ВµР В»Р Вµ" },
      { title: "Р вЂќР Р…Р ВµР Р†Р Р…Р С•Р в„– РЎРѓРЎвЂљР В°РЎвЂ Р С‘Р С•Р Р…Р В°РЎР‚", value: 3_600_000, deltaPct: -4, deltaSuffix: "Р В·Р В° 7 Р Т‘Р Р…Р ВµР в„–" },
      { title: "Р С™РЎР‚РЎС“Р С–Р В»Р С•РЎРѓРЎС“РЎвЂљР С•РЎвЂЎР Р…РЎвЂ№Р в„– РЎРѓРЎвЂљР В°РЎвЂ Р С‘Р С•Р Р…Р В°РЎР‚", value: 8_500_000, deltaPct: +8, deltaSuffix: "Р В·Р В° 7 Р Т‘Р Р…Р ВµР в„–" },
    ];

    return base.map((x, idx) => ({
      title: x.title,
      max,
      deltaPct: x.deltaPct,
      deltaSuffix: x.deltaSuffix,
      value: clamp(x.value + Math.round(120_000 * Math.sin(tick * 0.35 + idx)), 100_000, max),
    }));
  }, [tick]);

  return (
    <PageShell title="/dashboards/app/test_2 РІР‚вЂќ Р С”Р В°Р С” Р Р…Р В° РЎРѓР С”РЎР‚Р С‘Р Р…Р Вµ (Р В±Р ВµР В· Р С•Р Т‘Р С•Р СР ВµРЎвЂљРЎР‚Р В°)">
      {cards.map((c) => (
        <GaugeHalfCard key={c.title} {...c} />
      ))}
    </PageShell>
  );
}

/* ----------------------------- TEST 3 PAGE ----------------------------- */

function Test3Page(): JSX.Element {
  const max = 10_000_000;

  const cards: GaugeHalfCardProps[] = [
    { title: "Р С™Р С•Р В»Р С‘РЎвЂЎР ВµРЎРѓРЎвЂљР Р†Р С• РЎС“РЎРѓР В»РЎС“Р С– Р С’Р СџР Сџ", value: 4_800_000, max, deltaPct: -5, deltaSuffix: "Р С” Р С—РЎР‚Р С•РЎв‚¬Р В»Р С•Р в„– Р Р…Р ВµР Т‘Р ВµР В»Р Вµ" },
    { title: "Р вЂќР Р…Р ВµР Р†Р Р…Р С•Р в„– РЎРѓРЎвЂљР В°РЎвЂ Р С‘Р С•Р Р…Р В°РЎР‚", value: 7_300_000, max, deltaPct: +3, deltaSuffix: "Р В·Р В° 7 Р Т‘Р Р…Р ВµР в„–" },
    { title: "Р С™РЎР‚РЎС“Р С–Р В»Р С•РЎРѓРЎС“РЎвЂљР С•РЎвЂЎР Р…РЎвЂ№Р в„– РЎРѓРЎвЂљР В°РЎвЂ Р С‘Р С•Р Р…Р В°РЎР‚", value: 9_100_000, max, deltaPct: +14, deltaSuffix: "Р В·Р В° 7 Р Т‘Р Р…Р ВµР в„–" },
  ];

  return (
    <PageShell title="/dashboards/app/test_3 РІР‚вЂќ (РЎРѓРЎвЂљР С‘Р В»РЎРЉ Р С”Р В°Р С” test_2, Р Т‘Р ВµР СР С•-Р Р†Р В°РЎР‚Р С‘Р В°Р Р…РЎвЂљ)">
      {cards.map((c) => (
        <GaugeHalfCard key={c.title} {...c} />
      ))}
    </PageShell>
  );
}

/* ----------------------------- default export ----------------------------- */

export default function DashboardsTestPage(): JSX.Element {
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  if (path.includes("test_2")) return <Test2Page />;
  if (path.includes("test_3")) return <Test3Page />;
  return <Test1Page />;
}
